import React, { useState } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import { ArrowLineDown, ArrowCounterClockwise, DotsThreeVertical, Pause, Play, Spinner } from '@phosphor-icons/react'
import { Button, Icon, IconButton, Select, SelectContent, SelectItem, SelectTrigger, Tooltip, TooltipContent, TooltipTrigger } from '@shopflo/ui'
import { apiURI, constants } from 'src/lib/utils/constants'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import useGTM from 'src/lib/hooks/useGTM'
import { Report, ReportRun } from './types'
import { getReportRowState } from './reportFormatters'
import ShareModal from './ShareModal'

const authHeaders = () => ({
  baseURL: apiURI.CHECKOUT_NODE,
  headers: { Authorization: Cookies.get(constants.AUTH_COOKIE_CLIENT) ?? '' }
})

interface ReportRowActionsProps {
  report: Report
  onPauseClick: (args: { reportId: string; runId: string }) => void
  onResumeClick: (args: { reportId: string; runId: string }) => void
  onEdit?: (report: Report) => void
  onSetupRecurring?: (report: Report) => void
  mutateReports?: () => void
}

const ReportRowActions: React.FC<ReportRowActionsProps> = ({
  report,
  onPauseClick,
  onResumeClick,
  onEdit,
  onSetupRecurring,
  mutateReports
}) => {
  const { sendGTMTrigger } = useGTM()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)

  const state = getReportRowState(report)
  const latestRun = state.latestRun
  const isRecurring = report.frequencyType === 'RECURRING'
  const isPaused = report.status === 'paused'

  const handleDownload = async (runId: ReportRun['runId']) => {
    if (!runId) return
    try {
      setIsDownloading(true)
      const response = await axios.get(`/dashboard/report/download?runId=${runId}`, authHeaders())
      window.open(response.data.url, '_blank')
      sendGTMTrigger('sso_identified_contacts_report_downloaded', 'click')
    } catch (error) {
      console.error('report download failed', error)
      errorToast('Failed to download report')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleRetry = async (runId: ReportRun['runId']) => {
    if (!runId) return
    try {
      setIsRetrying(true)
      await axios.post(`/dashboard/report/${runId}/retry`, {}, authHeaders())
      successToast('Report retried successfully')
      mutateReports?.()
    } catch (error) {
      console.error('report retry failed', error)
      errorToast('Failed to retry report')
    } finally {
      setIsRetrying(false)
    }
  }

  const handleMenuAction = (value: string) => {
    switch (value) {
      case 'send':
        setShareOpen(true)
        break
      case 'edit':
        onEdit?.(report)
        break
      case 'setup_recurring':
        onSetupRecurring?.(report)
        break
      case 'pause':
        onPauseClick({ reportId: report.reportId, runId: latestRun?.runId ?? '' })
        break
      case 'resume':
        onResumeClick({ reportId: report.reportId, runId: latestRun?.runId ?? '' })
        break
    }
  }

  return (
    <div className='flex min-h-[48px] flex-1 items-center justify-end gap-0.5 px-2' onClick={e => e.stopPropagation()}>
      {state.status === 'generating' && (
        <span className='inline-flex items-center gap-1.5 text-text-xs-regular text-zinc-600'>
          <Icon IconComponent={Spinner} size='sm' className='shrink-0' iconProps={{ className: 'animate-spin' }} />
          Generating
        </span>
      )}

      <div className='relative flex items-center gap-0.5 self-stretch'>
        {/* Retry reveals on hover to the LEFT of the download/menu, with a gradient fade (like "View all"). */}
        {state.status === 'failed' && latestRun && (
          <div className='absolute bottom-0 right-full top-0 hidden items-stretch group-hover:flex'>
            <div className='w-10 bg-[linear-gradient(to_right,rgba(243,243,244,0),rgba(243,243,244,1))]' />
            <div className='flex items-center bg-[#f3f3f4] pr-1'>
              <Button
                variant='ghost'
                size='sm'
                leftIcon={ArrowCounterClockwise}
                loading={isRetrying}
                disabled={isRetrying}
                onClick={() => handleRetry(latestRun.runId)}
              >
                Retry
              </Button>
            </div>
          </div>
        )}

        {state.status !== 'generating' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <Button
                  variant='outline'
                  size='sm'
                  className='!h-7 !w-7 items-center justify-center !gap-0 !px-0 text-zinc-600 disabled:!opacity-100 disabled:!text-zinc-400'
                  loading={isDownloading}
                  disabled={latestRun?.status !== 'COMPLETED'}
                  onClick={() => latestRun && handleDownload(latestRun.runId)}
                  aria-label='Download report'
                >
                  <Icon IconComponent={ArrowLineDown} size='sm' />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent align='start'>Download</TooltipContent>
          </Tooltip>
        )}

        <Select value='' onValueChange={handleMenuAction}>
        <SelectTrigger
          className='flex h-8 w-8 items-center justify-center border-none bg-transparent p-0 shadow-none outline-none focus:ring-0'
          showCaret={false}
        >
          <IconButton
            IconComponent={DotsThreeVertical}
            size='sm'
            variant='ghost'
            iconProps={{ weight: 'bold' }}
          />
        </SelectTrigger>
        <SelectContent align='end' className='min-w-[200px]'>
          {!isRecurring && <SelectItem value='setup_recurring'>Set up Recurring Report</SelectItem>}
          {!isRecurring && <SelectItem value='send'>Send Report</SelectItem>}
          {isRecurring && <SelectItem value='edit'>Edit Report</SelectItem>}
          {/* -mx-1.5 breaks the divider out of the Viewport's fixed p-1.5 so it spans the full 200px. */}
          {isRecurring && <div className='-mx-1.5 my-1 border-t-2 border-dotted border-zinc-300/50' />}
          {isRecurring &&
            (isPaused ? (
              <SelectItem value='resume'>
                <span className='flex items-center gap-2'>
                  <Icon IconComponent={Play} size='sm' className='shrink-0' />
                  Resume Report
                </span>
              </SelectItem>
            ) : (
              <SelectItem value='pause'>
                <span className='flex items-center gap-2'>
                  <Icon IconComponent={Pause} size='sm' className='shrink-0' />
                  Pause Report
                </span>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      </div>

      {latestRun && <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} runId={latestRun.runId} />}
    </div>
  )
}

export default ReportRowActions
